/* crm-comm-analytics.jsx — Communication analytics dashboard for Phase 4
   Provides: CommunicationAnalyticsView
   Self-registers on window. */

const { useState, useEffect, useMemo, useRef, useCallback } = React;

/* ── helpers ── */
const _fmtNum = (n) => n >= 1000 ? (n / 1000).toFixed(1) + 'k' : String(n);
const _monthLabel = (d) => d.toLocaleString('default', { month: 'short' });

const _last6Months = () => {
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - i);
    months.push({ label: _monthLabel(d), year: d.getFullYear(), month: d.getMonth() });
  }
  return months;
};

const _daysSince = (iso) => {
  if (!iso) return 999;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
};

const _last30Days = () => Date.now() - 30 * 86400000;

/* ══════════════════════════════════════════════════════════════
   STAT CARD
   ══════════════════════════════════════════════════════════════ */
const CommStatCard = ({ label, value, icon, color, sub }) => (
  <div style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 10, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 32, height: 32, borderRadius: 8, background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <NexusIcon name={icon} size={15} style={{ color }} />
      </div>
      <span style={{ fontSize: 12, color: 'var(--nx-text-muted)', fontWeight: 600 }}>{label}</span>
    </div>
    <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--nx-text)' }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{sub}</div>}
  </div>
);

/* ══════════════════════════════════════════════════════════════
   ENGAGEMENT SCORE GAUGE (circle SVG)
   ══════════════════════════════════════════════════════════════ */
const EngagementGauge = ({ score }) => {
  const r = 44, cx = 54, cy = 54;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  const color = score >= 70 ? '#10B981' : score >= 40 ? '#F59E0B' : '#EF4444';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 10, padding: '16px 18px' }}>
      <svg width={108} height={108} viewBox="0 0 108 108">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--nx-border)" strokeWidth={10} />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={10} strokeLinecap="round"
          strokeDasharray={`${dash} ${circ}`} strokeDashoffset={circ / 4}
          style={{ transition: 'stroke-dasharray 0.5s ease' }} />
        <text x={cx} y={cy + 7} textAnchor="middle" fontSize={22} fontWeight={800} fill={color}>{score}</text>
      </svg>
      <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', fontWeight: 600 }}>Engagement Score</div>
      <div style={{ fontSize: 11, color }}>
        {score >= 70 ? 'High' : score >= 40 ? 'Medium' : 'Low'}
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   TREND CHART (SVG 6-month dual-line)
   ══════════════════════════════════════════════════════════════ */
const TrendChart = ({ months, emailData, meetingData }) => {
  const W = 480, H = 160, padL = 30, padB = 24, padT = 12, padR = 16;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const maxVal = Math.max(...emailData, ...meetingData, 1);
  const n = months.length;

  const xOf = (i) => padL + (i / (n - 1)) * plotW;
  const yOf = (v) => padT + plotH - (v / maxVal) * plotH;

  const polyPoints = (data) => data.map((v, i) => `${xOf(i)},${yOf(v)}`).join(' ');

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow: 'visible' }}>
      {/* grid lines */}
      {[0, 0.25, 0.5, 0.75, 1].map(t => {
        const y = padT + plotH * (1 - t);
        return <line key={t} x1={padL} y1={y} x2={W - padR} y2={y} stroke="var(--nx-border)" strokeWidth={0.8} />;
      })}
      {/* x labels */}
      {months.map((m, i) => (
        <text key={m.label} x={xOf(i)} y={H - 6} textAnchor="middle" fontSize={10} fill="var(--nx-text-muted)">{m.label}</text>
      ))}
      {/* email line */}
      <polyline points={polyPoints(emailData)} fill="none" stroke="#2563EB" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      {emailData.map((v, i) => <circle key={i} cx={xOf(i)} cy={yOf(v)} r={3.5} fill="#2563EB" />)}
      {/* meeting line */}
      <polyline points={polyPoints(meetingData)} fill="none" stroke="#7C3AED" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      {meetingData.map((v, i) => <circle key={i} cx={xOf(i)} cy={yOf(v)} r={3.5} fill="#7C3AED" />)}
      {/* legend */}
      <circle cx={padL + 8} cy={padT - 2} r={5} fill="#2563EB" />
      <text x={padL + 18} y={padT + 2} fontSize={10} fill="var(--nx-text-muted)">Emails</text>
      <circle cx={padL + 68} cy={padT - 2} r={5} fill="#7C3AED" />
      <text x={padL + 78} y={padT + 2} fontSize={10} fill="var(--nx-text-muted)">Meetings</text>
    </svg>
  );
};

/* ══════════════════════════════════════════════════════════════
   INLINE ACTIVITY HEATMAP
   ══════════════════════════════════════════════════════════════ */
const InlineHeatmap = ({ activity }) => {
  const DAYS = ['S','M','T','W','T','F','S'];
  const WEEKS = 4;
  const now = new Date();
  const grid = useMemo(() => {
    const map = {};
    activity.forEach(a => {
      const d = new Date(a.createdAt || a.date || 0);
      const diff = Math.floor((now - d) / 86400000);
      if (diff < WEEKS * 7) map[diff] = (map[diff] || 0) + 1;
    });
    const cells = [];
    for (let w = WEEKS - 1; w >= 0; w--) {
      for (let day = 0; day < 7; day++) {
        const daysBack = w * 7 + (6 - now.getDay() + day + 1) % 7;
        cells.push({ w: WEEKS - 1 - w, day, count: map[daysBack] || 0 });
      }
    }
    return cells;
  }, [activity]);
  const maxCount = Math.max(...grid.map(c => c.count), 1);
  const cellSize = 18, gap = 3;
  return (
    <svg width={WEEKS * (cellSize + gap) + 26} height={7 * (cellSize + gap) + 16} style={{ overflow: 'visible' }}>
      {DAYS.map((d, i) => (
        <text key={d + i} x={0} y={i * (cellSize + gap) + cellSize / 2 + 5} fontSize={9} fill="var(--nx-text-muted)">{d}</text>
      ))}
      {grid.map((c, i) => {
        const x = c.w * (cellSize + gap) + 22;
        const y = c.day * (cellSize + gap);
        const opacity = c.count === 0 ? 0.1 : 0.2 + (c.count / maxCount) * 0.8;
        return <rect key={i} x={x} y={y} width={cellSize} height={cellSize} rx={3} fill="#6366f1" opacity={opacity}><title>{c.count} events</title></rect>;
      })}
    </svg>
  );
};

/* ══════════════════════════════════════════════════════════════
   COMMUNICATION ANALYTICS VIEW
   ══════════════════════════════════════════════════════════════ */
const CommunicationAnalyticsView = () => {
  const wsId = window.__nx_auth?.user?.activeWorkspaceId;
  const store = useStore();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (window.__nx_api?.commAnalytics?.getCommunication) {
          const data = await window.__nx_api.commAnalytics.getCommunication(wsId);
          if (data) setSummary(data);
        }
      } catch(e) { /* keep computed fallback */ }
      setLoading(false);
    })();
  }, [wsId]);

  const activity = useMemo(() => store?.activity || [], [store]);

  /* --- derive stats from store when API unavailable --- */
  const emailsSent     = summary?.emailsSent     ?? activity.filter(a => a.type === 'email' && a.direction !== 'INCOMING').length;
  const emailsReceived = summary?.emailsReceived ?? activity.filter(a => a.type === 'email' && a.direction === 'INCOMING').length;
  const totalMeetings  = summary?.totalMeetings  ?? activity.filter(a => a.type === 'meeting').length;
  const totalCalls     = summary?.totalCalls     ?? activity.filter(a => a.type === 'call').length;
  const engagementScore = summary?.engagementScore ?? Math.min(100, Math.round(((emailsSent + emailsReceived + totalMeetings * 2 + totalCalls) / Math.max(activity.length || 1, 1)) * 40 + 30));

  /* --- 6-month trend --- */
  const months = useMemo(() => _last6Months(), []);
  const trendData = useMemo(() => {
    const emailsByMonth = Array(6).fill(0);
    const meetingsByMonth = Array(6).fill(0);
    activity.forEach(a => {
      const d = new Date(a.createdAt || a.date || 0);
      months.forEach((m, i) => {
        if (d.getFullYear() === m.year && d.getMonth() === m.month) {
          if (a.type === 'email') emailsByMonth[i]++;
          if (a.type === 'meeting') meetingsByMonth[i]++;
        }
      });
    });
    return { emailsByMonth, meetingsByMonth };
  }, [activity, months]);

  /* --- response time --- */
  const avgResponseTime = useMemo(() => {
    const inbound = activity.filter(a => a.type === 'email' && a.direction === 'INCOMING').sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
    const outbound = activity.filter(a => a.type === 'email' && a.direction !== 'INCOMING');
    const diffs = [];
    inbound.forEach(inb => {
      const inbTime = new Date(inb.createdAt || 0).getTime();
      const nextOut = outbound.find(o => new Date(o.createdAt || 0).getTime() > inbTime);
      if (nextOut) diffs.push((new Date(nextOut.createdAt || 0).getTime() - inbTime) / 3600000);
    });
    if (!diffs.length) return null;
    return Math.round(diffs.reduce((s, d) => s + d, 0) / diffs.length);
  }, [activity]);

  /* --- record type breakdown --- */
  const emailsDeals    = activity.filter(a => a.type === 'email' && a.dealId).length;
  const emailsContacts = activity.filter(a => a.type === 'email' && (a.personId || a.contactId) && !a.dealId).length;
  const meetingsDeals  = activity.filter(a => a.type === 'meeting' && a.dealId).length;

  /* --- top active accounts --- */
  const topAccounts = useMemo(() => {
    const threshold = _last30Days();
    const recent = activity.filter(a => new Date(a.createdAt || a.date || 0).getTime() > threshold);
    const map = {};
    recent.forEach(a => {
      const key = a.companyId || a.companyName;
      if (!key) return;
      map[key] = map[key] || { id: a.companyId, name: a.companyName || a.companyId, count: 0 };
      map[key].count++;
    });
    return Object.values(map).sort((a, b) => b.count - a.count).slice(0, 8);
  }, [activity]);

  const sectionHead = (title) => (
    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--nx-text)', marginBottom: 14 }}>{title}</div>
  );

  const card = (children, style = {}) => (
    <div style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 10, padding: '18px 20px', ...style }}>
      {children}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, padding: 24, overflowY: 'auto', height: '100%', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <NexusIcon name="BarChart2" size={20} style={{ color: 'var(--nx-accent)' }} />
        <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--nx-text)' }}>Communication Analytics</span>
      </div>

      {/* 1. Summary stat cards */}
      <div>
        {sectionHead('Overview')}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
          <CommStatCard label="Emails Sent"     value={_fmtNum(emailsSent)}     icon="Send"     color="#2563EB" />
          <CommStatCard label="Emails Received" value={_fmtNum(emailsReceived)} icon="Inbox"    color="#10B981" />
          <CommStatCard label="Meetings"        value={_fmtNum(totalMeetings)}  icon="Calendar" color="#7C3AED" />
          <CommStatCard label="Calls"           value={_fmtNum(totalCalls)}     icon="Phone"    color="#0EA5E9" />
          <EngagementGauge score={engagementScore} />
        </div>
      </div>

      {/* 2. Trend chart */}
      {card(
        <>
          {sectionHead('6-Month Communication Trend')}
          <TrendChart months={months} emailData={trendData.emailsByMonth} meetingData={trendData.meetingsByMonth} />
        </>
      )}

      {/* 3. Response time */}
      {card(
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <NexusIcon name="Clock" size={20} style={{ color: '#2563EB' }} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', fontWeight: 600, marginBottom: 4 }}>AVG RESPONSE TIME</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--nx-text)' }}>
              {avgResponseTime == null ? '—' : avgResponseTime < 1 ? '<1h' : avgResponseTime + 'h'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>
              {avgResponseTime == null ? 'Insufficient email thread data' : 'Average time to reply to inbound emails'}
            </div>
          </div>
        </div>
      )}

      {/* 4. By record type */}
      {card(
        <>
          {sectionHead('Communication by Record Type')}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
            {[
              { label: 'Emails linked to Deals',    value: emailsDeals,    icon: 'Target',   color: '#6366f1' },
              { label: 'Emails linked to Contacts', value: emailsContacts, icon: 'User',     color: '#0EA5E9' },
              { label: 'Meetings with Deals',       value: meetingsDeals,  icon: 'Calendar', color: '#7C3AED' },
            ].map(s => (
              <div key={s.label} style={{ background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                  <NexusIcon name={s.icon} size={13} style={{ color: s.color }} />
                  <span style={{ fontSize: 11, color: 'var(--nx-text-muted)', fontWeight: 600 }}>{s.label}</span>
                </div>
                <div style={{ fontSize: 24, fontWeight: 800, color: s.color }}>{s.value}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* 5. Top active accounts */}
      {card(
        <>
          {sectionHead('Top Active Accounts (Last 30 Days)')}
          {topAccounts.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--nx-text-muted)', textAlign: 'center', padding: 24 }}>No account activity recorded in last 30 days</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--nx-surface-alt)' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', borderBottom: '1px solid var(--nx-border)' }}>Company</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', borderBottom: '1px solid var(--nx-border)' }}>Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {topAccounts.map((a, i) => (
                    <tr key={a.id || i} onClick={() => store?.openDrawer?.(a)} style={{ borderBottom: '1px solid var(--nx-border)', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--nx-surface-alt)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '10px 12px', fontWeight: 600, color: 'var(--nx-text)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 28, height: 28, borderRadius: 6, background: `hsl(${i * 37}, 60%, 88%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: `hsl(${i * 37}, 60%, 40%)`, flexShrink: 0 }}>
                            {(a.name || '?').charAt(0).toUpperCase()}
                          </span>
                          {a.name || a.id}
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 20, background: '#EFF6FF', color: '#2563EB', fontWeight: 700, fontSize: 12 }}>
                          <NexusIcon name="Activity" size={10} /> {a.count}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* 6. Activity heatmap */}
      {card(
        <>
          {sectionHead('Activity Heatmap (Last 4 Weeks)')}
          {typeof window.AnalyticsP3View !== 'undefined'
            ? <div style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>Heatmap available in Analytics dashboard.</div>
            : <InlineHeatmap activity={activity} />
          }
        </>
      )}
    </div>
  );
};

/* ── registration ── */
window.CommunicationAnalyticsView = CommunicationAnalyticsView;
